import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { toast } from '../../hooks/use-toast';
import { Award, Clock, Check, AlertTriangle, ChevronRight, Star, ArrowLeft, Send } from 'lucide-react';
import useBlinkHighlight from '../../hooks/useBlinkHighlight';

const API = process.env.REACT_APP_BACKEND_URL;

const formatDate = (d) => {
  if (!d) return '';
  try { return new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

const daysUntil = (d) => {
  if (!d) return null;
  const target = new Date(d + 'T23:59:59');
  return Math.ceil((target - new Date()) / 86400000);
};

const PortalAppraisal = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('portal_token');
  const headers = { Authorization: `Bearer ${token}` };

  const [cycle, setCycle] = useState(null);
  const [myReview, setMyReview] = useState(null);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('home'); // home | self | manager
  const [reviewingEmp, setReviewingEmp] = useState(null);
  const cycleRefs = useRef({});
  // Highlight the active cycle banner if the page is opened via a
  // notification deep-link carrying ?highlight=<cycle_id>. Also matches
  // a generic 'active' key so the routing layer can blink without
  // knowing the cycle id.
  useBlinkHighlight(cycleRefs, !loading && !!cycle);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const cycleRes = await axios.get(`${API}/api/appraisals/cycles/active`, { headers });
      const c = cycleRes.data;
      setCycle(c);
      if (c?.id) {
        const [meRes, teamRes] = await Promise.all([
          axios.get(`${API}/api/appraisals/self-review/me?cycle_id=${c.id}`, { headers }),
          axios.get(`${API}/api/appraisals/manager-review/my-team?cycle_id=${c.id}`, { headers }),
        ]);
        setMyReview(meRes.data);
        setTeam(teamRes.data || []);
      }
    } catch (e) {
      if (e?.response?.status === 401) navigate('/employee-portal/login');
    } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="p-6 text-center text-gray-500" data-testid="appraisal-loading">Loading…</div>;

  if (!cycle) return (
    <div className="p-6" data-testid="no-active-cycle">
      <button onClick={() => navigate('/employee-portal')} className="flex items-center gap-1 text-sm text-violet-600 mb-4">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <Card className="border-dashed">
        <CardContent className="py-12 text-center">
          <Award className="h-10 w-10 mx-auto text-amber-400 mb-3" />
          <p className="text-gray-600">No active appraisal cycle right now.</p>
        </CardContent>
      </Card>
    </div>
  );

  const dleft = daysUntil(cycle.deadline_date);
  const pendingMgr = team.filter(t => !t.reviewed).length;

  if (view === 'self') {
    return (
      <SelfReviewForm
        cycle={cycle}
        existing={myReview}
        onBack={() => { setView('home'); load(); }}
      />
    );
  }
  if (view === 'manager' && reviewingEmp) {
    return (
      <ManagerReviewForm
        cycle={cycle}
        employee={reviewingEmp}
        onBack={() => { setView('home'); setReviewingEmp(null); load(); }}
      />
    );
  }

  return (
    <div className="p-4 space-y-4 max-w-2xl mx-auto" data-testid="portal-appraisal">
      <button onClick={() => navigate('/employee-portal')} className="flex items-center gap-1 text-sm text-violet-600">
        <ArrowLeft className="h-4 w-4" /> Back to Home
      </button>

      {/* Cycle banner */}
      <Card
        ref={(el) => { cycleRefs.current[cycle.id] = el; cycleRefs.current.active = el; }}
        className="border-0 bg-gradient-to-br from-amber-100 via-orange-50 to-rose-50 shadow-sm"
      >
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Award className="h-8 w-8 text-amber-500 mt-1" />
            <div className="flex-1">
              <h2 className="text-lg font-bold text-gray-900">{cycle.name}</h2>
              <p className="text-xs text-gray-600 mt-0.5">Deadline: {formatDate(cycle.deadline_date)}</p>
              {dleft !== null && (
                <Badge className={`mt-2 ${dleft < 0 ? 'bg-rose-500' : dleft <= 3 ? 'bg-orange-500' : 'bg-emerald-500'} text-white`}>
                  <Clock className="h-3 w-3 mr-1" />
                  {dleft < 0 ? `Overdue by ${Math.abs(dleft)} day(s)` : dleft === 0 ? 'Due today' : `${dleft} day(s) left`}
                </Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Self Review */}
      <Card className="border-0 shadow-sm cursor-pointer hover:shadow-md transition" onClick={() => setView('self')} data-testid="self-review-card">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className={`h-12 w-12 rounded-full flex items-center justify-center ${myReview ? 'bg-emerald-100' : 'bg-sky-100'}`}>
              {myReview ? <Check className="h-6 w-6 text-emerald-600" /> : <Star className="h-6 w-6 text-sky-600" />}
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900">My Self-Review</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {myReview
                  ? `Submitted • Score ${myReview.percentage}% (you can update before deadline)`
                  : `${cycle.staff_questions?.length || 0} questions · share your feedback`}
              </p>
            </div>
            <ChevronRight className="h-5 w-5 text-gray-400" />
          </div>
        </CardContent>
      </Card>

      {/* Manager Review section - shown only if user has direct reports */}
      {team.length > 0 && (
        <Card className="border-0 shadow-sm" data-testid="manager-section">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                Reviews of My Team
                {pendingMgr > 0 && (
                  <Badge className="bg-rose-500 text-white" data-testid="pending-badge">
                    <AlertTriangle className="h-3 w-3 mr-1" /> {pendingMgr} pending
                  </Badge>
                )}
              </h3>
              <span className="text-xs text-gray-500">{team.length - pendingMgr}/{team.length} done</span>
            </div>
            <div className="space-y-2">
              {team.map(emp => (
                <button
                  key={emp.id}
                  onClick={() => { setReviewingEmp(emp); setView('manager'); }}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border transition text-left ${emp.reviewed ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50/40 border-amber-200 hover:bg-amber-50'}`}
                  data-testid={`team-row-${emp.id}`}
                >
                  <div className={`h-10 w-10 rounded-full flex items-center justify-center ${emp.reviewed ? 'bg-emerald-200' : 'bg-amber-200'}`}>
                    {emp.reviewed ? <Check className="h-5 w-5 text-emerald-700" /> : <Star className="h-5 w-5 text-amber-700" />}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-900">{emp.name}</p>
                    <p className="text-[11px] text-gray-500">{emp.employee_code} • {emp.department}</p>
                  </div>
                  {emp.reviewed
                    ? <span className="text-xs font-bold text-emerald-700">{emp.percentage}%</span>
                    : <span className="text-xs font-bold text-rose-600">Pending</span>}
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ── Self Review Form ───────────────────────────────────────────────────
const SelfReviewForm = ({ cycle, existing, onBack }) => {
  const initial = (cycle.staff_questions || []).map((q, i) => ({
    question: q,
    answer: existing?.answers?.[i]?.answer || '',
  }));
  const [answers, setAnswers] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const token = localStorage.getItem('portal_token');
  const headers = { Authorization: `Bearer ${token}` };

  const submit = async () => {
    if (answers.some(a => !a.answer.trim())) {
      return toast({ title: 'Please answer all questions', variant: 'destructive' });
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/appraisals/self-review`, {
        cycle_id: cycle.id, answers,
      }, { headers });
      toast({ title: 'Self-review submitted', description: 'Your responses are scored by AI for positivity.' });
      onBack();
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  return (
    <div className="p-4 space-y-4 max-w-2xl mx-auto" data-testid="self-review-form">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-violet-600">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <h2 className="text-lg font-bold text-gray-900 mb-1">Self-Review</h2>
          <p className="text-xs text-gray-500 mb-4">Be candid — your answers are scored for tone and constructiveness by AI on a 0–5 scale.</p>
          <div className="space-y-4">
            {answers.map((a, i) => (
              <div key={i}>
                <label className="text-sm font-medium text-gray-800 block mb-1">{i + 1}. {a.question}</label>
                <Textarea
                  rows={4}
                  value={a.answer}
                  onChange={e => setAnswers(prev => prev.map((x, j) => j === i ? { ...x, answer: e.target.value } : x))}
                  placeholder="Share your thoughts…"
                  data-testid={`self-q-${i}`}
                />
              </div>
            ))}
          </div>
          <Button onClick={submit} disabled={submitting} className="w-full mt-5 bg-amber-600 hover:bg-amber-700" data-testid="self-submit">
            <Send className="h-4 w-4 mr-1" /> {submitting ? 'Submitting…' : existing ? 'Update Self-Review' : 'Submit Self-Review'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

// ── Manager Review Form ────────────────────────────────────────────────
const ManagerReviewForm = ({ cycle, employee, onBack }) => {
  const token = localStorage.getItem('portal_token');
  const headers = { Authorization: `Bearer ${token}` };
  const [answers, setAnswers] = useState((cycle.manager_questions || []).map(q => ({
    question: q, score: 3, note: '',
  })));
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/appraisals/manager-review/staff?cycle_id=${cycle.id}&employee_id=${employee.id}`, { headers });
        if (r.data?.answers?.length) setAnswers(r.data.answers);
      } catch { /* ignore */ } finally { setLoading(false); }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/appraisals/manager-review`, {
        cycle_id: cycle.id, employee_id: employee.id, answers,
      }, { headers });
      toast({ title: 'Review submitted', description: `${employee.name} has been appraised.` });
      onBack();
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="p-6 text-center text-gray-500">Loading…</div>;

  return (
    <div className="p-4 space-y-4 max-w-2xl mx-auto" data-testid="manager-review-form">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-violet-600">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <h2 className="text-lg font-bold text-gray-900">Review: {employee.name}</h2>
          <p className="text-xs text-gray-500 mb-4">{employee.employee_code} • {employee.department}</p>
          <div className="space-y-5">
            {answers.map((a, i) => (
              <div key={i} className="border-b pb-4 last:border-0">
                <label className="text-sm font-medium text-gray-800 block mb-2">{i + 1}. {a.question}</label>
                <div className="flex items-center gap-1 mb-2" data-testid={`mgr-stars-${i}`}>
                  {[1, 2, 3, 4, 5].map(n => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setAnswers(prev => prev.map((x, j) => j === i ? { ...x, score: n } : x))}
                      className="p-1 hover:scale-110 transition"
                      data-testid={`mgr-star-${i}-${n}`}
                    >
                      <Star className={`h-7 w-7 ${n <= a.score ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
                    </button>
                  ))}
                  <span className="ml-2 text-sm font-bold text-amber-700">{a.score}/5</span>
                </div>
                <Textarea
                  rows={2}
                  value={a.note || ''}
                  onChange={e => setAnswers(prev => prev.map((x, j) => j === i ? { ...x, note: e.target.value } : x))}
                  placeholder="Optional note…"
                  className="text-sm"
                  data-testid={`mgr-note-${i}`}
                />
              </div>
            ))}
          </div>
          <Button onClick={submit} disabled={submitting} className="w-full mt-5 bg-violet-600 hover:bg-violet-700" data-testid="mgr-submit">
            <Send className="h-4 w-4 mr-1" /> {submitting ? 'Submitting…' : 'Submit Review'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default PortalAppraisal;
